import { Box, Button } from "@garther/ui/components";
import { Home, RefreshCw, XCircle } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

interface FriendInvitationErrorProps {
  message: string;
}

export const FriendInvitationError: React.FC<FriendInvitationErrorProps> = ({ message }) => {
  const { t } = useTranslation();

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="h-full e-center bg-background-primary">
      <div className="max-w-md w-full mx-4">
        <Box>
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full e-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="heading-2 text-text-primary mb-4">{t("friendInvitation.errorTitle")}</h1>
            <p className="body-base text-text-secondary mb-6">{message}</p>
          </div>

          <div className="space-y-4">
            {/* Error Details */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-medium mb-2">{t("friendInvitation.whatWentWrong")}</h3>
              <p className="text-red-700 text-sm">{t("friendInvitation.errorExplanation")}</p>
            </div>

            {/* Possible Solutions */}
            <div className="bg-background-highlight rounded-lg p-4 border border-border-light">
              <h4 className="text-text-primary font-medium mb-3">
                {t("friendInvitation.possibleSolutions")}
              </h4>
              <ul className="text-text-secondary text-sm space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  {t("friendInvitation.solution1")}
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  {t("friendInvitation.solution2")}
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  {t("friendInvitation.solution3")}
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button variant="default" onClick={handleRetry} className="w-full e-center space-x-2">
                <RefreshCw className="w-4 h-4" />
                <span>{t("common.retry")}</span>
              </Button>

              <Button variant="outline" onClick={handleGoHome} className="w-full e-center space-x-2">
                <Home className="w-4 h-4" />
                <span>{t("navigation.home")}</span>
              </Button>
            </div>

            {/* Contact Support */}
            <div className="text-center pt-4 border-t border-border-light">
              <p className="text-xs text-text-secondary">
                {t("friendInvitation.needHelp")}{" "}
                <a
                  href="mailto:support@garther.com"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  {t("friendInvitation.contactSupport")}
                </a>
              </p>
            </div>
          </div>
        </Box>
      </div>
    </div>
  );
};
